import React, { useState } from 'react';
import { Music, Radio, Youtube, Rss, Mail, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { platformLinks } from '../data/mockData';

const Subscribe = () => {
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      // Mock subscription
      toast({
        title: "Successfully Subscribed!",
        description: `We'll send updates to ${email}`,
        duration: 3000,
      });
      setEmail('');
    }
  };

  const platforms = [
    { name: 'Spotify', icon: Music, color: 'from-green-500 to-green-600', url: '#' },
    { name: 'Apple Podcasts', icon: Radio, color: 'from-purple-500 to-pink-500', url: '#' },
    { name: 'YouTube', icon: Youtube, color: 'from-red-500 to-red-600', url: '#' },
    { name: 'Google Podcasts', icon: Radio, color: 'from-blue-500 to-blue-600', url: '#' },
    { name: 'RSS Feed', icon: Rss, color: 'from-orange-500 to-orange-600', url: '#' }
  ];

  return (
    <section id="subscribe" className="py-24 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-4">
            Subscribe & Listen
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Never miss an episode. Subscribe on your favorite platform.
          </p>
        </div>

        {/* Email Subscription */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/20 rounded-2xl p-8 mb-12">
          <div className="text-center mb-6">
            <Mail className="h-12 w-12 text-purple-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Get Episode Updates</h3>
            <p className="text-gray-400">Join our email list for new episode notifications and exclusive content</p>
          </div>
          
          <form onSubmit={handleSubscribe} className="max-w-md mx-auto">
            <div className="flex gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500 transition-colors"
                required
              />
              <Button
                type="submit"
                className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white px-8 transition-all duration-300 shadow-lg shadow-purple-500/30"
              >
                Subscribe
              </Button>
            </div>
          </form>
        </div>

        {/* Platform Links */}
        <div>
          <h3 className="text-2xl font-bold text-white text-center mb-8">Listen on Your Favorite Platform</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {platforms.map((platform, index) => {
              const Icon = platform.icon;
              return (
                <a
                  key={index}
                  href={platform.url}
                  className="group bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl p-6 hover:border-purple-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20"
                >
                  <div className="text-center space-y-3">
                    <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r ${platform.color} rounded-full transition-transform group-hover:scale-110`}>
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <p className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                      {platform.name}
                    </p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div className="space-y-2">
            <CheckCircle className="h-8 w-8 text-purple-400 mx-auto" />
            <h4 className="text-white font-semibold">New Episodes Weekly</h4>
            <p className="text-gray-400 text-sm">Fresh content every week</p>
          </div>
          <div className="space-y-2">
            <CheckCircle className="h-8 w-8 text-purple-400 mx-auto" />
            <h4 className="text-white font-semibold">Exclusive Interviews</h4>
            <p className="text-gray-400 text-sm">Industry insights & stories</p>
          </div>
          <div className="space-y-2">
            <CheckCircle className="h-8 w-8 text-purple-400 mx-auto" />
            <h4 className="text-white font-semibold">Community Access</h4>
            <p className="text-gray-400 text-sm">Join the conversation</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Subscribe;
